<script>
import BoxedLoader from '@maison/components/common/BoxedLoader';
import PhoenixForm from '@phoenix/mixins/components/form/PhoenixForm';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { useForm } from 'vee-validate';
import { defineAsyncComponent, provide } from 'vue';

export default {
  components: {
    BoxedLoader,
    PhxsgButton,
    ChinesePIPL: defineAsyncComponent(() => import('@maison/components/form/fields/ChinesePIPL')),
    KoreanGDPR: defineAsyncComponent(() => import('@maison/components/form/KoreanGDPR')),
  },

  mixins: [PhoenixForm],

  props: {
    submitButtonFull: {
      type: Boolean,
      default: false,
    },
    isSubmitOnLeft: {
      type: Boolean,
      default: false,
    },
    actionsPlacement: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'start', 'stretch'].includes(value),
    },
    naked: {
      type: Boolean,
      default: false,
    },
    shouldDisplayFilledCheckmark: {
      type: Boolean,
      default: false,
    },
    submitButtonContainerClasses: {
      type: String,
      default: '',
    },
    shouldDisplayMandatoryFieldsText: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { setValues, validate } = useForm();

    provide('shouldDisplayFilledCheckmark', props.shouldDisplayFilledCheckmark);

    return { setValues, validate };
  },

  data() {
    return {
      isFormVisible: false,
    };
  },

  computed: {
    computedSubmitButtonContainerClasses() {
      if (this.submitButtonContainerClasses) {
        return [this.submitButtonContainerClasses];
      }

      return [
        'form-actions mt-base',
        {
          [`form-actions--${this.actionsPlacement}`]: this.actionsPlacement !== 'default',
        },
      ];
    },
  },

  watch: {
    /**
     * As long as the recaptcha is not fully loaded, we won't have access to the recaptcha badge. So once it's loaded
     * we set its opacity according to the visibility of the form.
     */
    recaptcha(value) {
      if (value !== null) {
        this.setCaptchaV3Visibility();
      }
    },
  },

  methods: {
    visibilityChanged(isVisible) {
      this.isFormVisible = isVisible;
      this.setCaptchaV3Visibility();
    },

    setCaptchaV3Visibility() {
      const greCaptchaBadge = document.querySelector('.grecaptcha-badge');
      if (!greCaptchaBadge) {
        return;
      }
      greCaptchaBadge.style.opacity = this.isFormVisible ? '100' : '0';
    },
  },
};
</script>

<template>
  <div v-observe-visibility="isCaptchaEnabled ? visibilityChanged : false">
    <form ref="form" @submit.prevent="submit">
      <div v-if="isLoading">
        <boxed-loader box-size="xs" />
      </div>

      <slot v-else-if="naked" :should-display-newsletter="!shouldDisplayKoreanGDPR" />

      <div v-else>
        <slot :should-display-newsletter="!shouldDisplayKoreanGDPR" />

        <korean-g-d-p-r
          v-if="shouldDisplayKoreanGDPR"
          :should-handle-newsletter="shouldHandleKoreanGDPRNewsletter"
          :initial-values="formValues"
          :form-type="koreanFormType"
          @change="onKoreanGDPRChange"
          @consent-values-loaded="onConsentValuesLoaded"
        />

        <chinese-p-i-p-l
          v-if="shouldDisplayPIPLConsentCheckboxes"
          :value="chinesePiplConsents"
          :form-type="formType"
          :custom-inner-class="customConsentInnerClass"
          :custom-outer-class="customConsentOuterClass"
          @on-chinese-pipl-consent-change="onChinesePiplConsentChange"
        />

        <div v-if="!showErrorsInToast && mainErrors" class="alert alert--danger mt-md text-center" role="alert">
          {{ mainErrors }}
        </div>

        <span v-if="shouldDisplayMandatoryFieldsText" class="field-helper-text mt-sm">
          {{ $t('Form.mandatoryFields') }}
        </span>

        <div v-if="shouldShowFormActions" :class="computedSubmitButtonContainerClasses">
          <slot name="form-actions" :is-disabled="isDisabled" :should-display-newsletter="!shouldDisplayKoreanGDPR" />

          <PhxsgButton
            v-if="shouldDisplaySubmitButton"
            ref="submitButton"
            :disabled="isDisabled"
            variant="plain"
            color="primary"
            :full="submitButtonFull"
            type="submit"
            data-cy="form-submit-btn"
          >
            {{ submitText }}
          </PhxsgButton>

          <PhxsgButton
            v-if="shouldDisplayCancelButton"
            ref="cancelButton"
            :disabled="isDisabled"
            variant="plain"
            color="secondary"
            data-cy="form-cancel-btn"
            @click.prevent="onCancelCallback"
          >
            {{ cancelText }}
          </PhxsgButton>

          <div
            v-if="shouldDisplayTermsLink"
            class="form-actions__terms rich-text privacy-sentence"
            v-html="termsLink"
          />

          <slot name="footer-actions-text" />

          <div
            v-if="shouldDisplayPIPLStatement"
            class="form-actions__terms rich-text"
            data-cy="pipl-statement"
            v-html="piplStatement"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<style>
.grecaptcha-badge {
  opacity: 0;
  z-index: 4000;
}
</style>
